/* global YT */

import { loadScript, on, select, selectAll, addClass, removeClass } from 'helpers/dom'
import debounce from 'lodash.debounce'

const selectors = {
  VIDEO_THUMBNAIL_SELECTOR: '.js-video-player-thumbnail',
  VIDEO_THUMBNAIL_BUTTON_SELECTOR: '.js-video-player-thumbnail-button',
  IMAGE_PREVIEW_SELECTOR: '.js-image-preview',
  HIDDEN_CLASS: 'opacity-0',
  PLAYING_CLASS: 'is-playing',
  LOADING_CLASS: 'is-loading',
  PAUSING_CLASS: 'is-pausing',
  VIDEO_SOUND_SELECTOR: '.js-sound-video',
  VIDEO_PLAY_SELECTOR: '.js-play-video',
  MODAL_VIDEO_SELECTOR: 'modal-dialog-video',
  BUTTON_CLOSE_MODAL_SELECTOR: '.js-modal-video .js-close-modal-dialog',
  MUTED_CLASS: 'is-muted',
  VIDEO_TYPES: {
    YOUTUBE: 'youtube',
    VIMEO: 'vimeo',
    MP4: 'mp4'
  },
  STATES: {
    PLAYING: 'playing',
    PAUSING: 'pausing',
    LOADING: 'loading'
  },
  DEBOUNCE_TIME: 300,
  DEFAULT_VIDEO_RATIO: 16 / 9,
  VIDEO_INNER_CLASS: ['absolute', 'inset-0', 'size-full', 'pointer-events-none', 'bg-transparent']
}

export default class VideoPlayer extends HTMLElement {
  constructor() {
    super()
    this.player = null
    this.element = document.createElement('div')
    this.videoInnerElement = document.createElement('div')
    this.type = this.getAttribute('data-video-type') || selectors.VIDEO_TYPES.MP4
    this.thumbnailEl = null
    this.imagePreviewEl = this.querySelectorAll(selectors.IMAGE_PREVIEW_SELECTOR)
    this.isLoaded = false
    this.src = this.getAttribute('src') || null
    this.playOnClick = false
    this.status = {
      play: null,
      error: null
    }
    this.options = {
      autoplay: this.hasAttribute('autoplay'),
      controls: this.hasAttribute('controls'),
      playsinline: this.hasAttribute('playsinline'),
      muted: this.hasAttribute('muted'),
      loop: this.hasAttribute('loop'),
      origin: window.location.origin,
      thumbnail: this.getAttribute('data-thumbnail'),
      loading: this.getAttribute('data-loading'),
      playIntersection: this.hasAttribute('data-play-intersection'),
      lazyload: this.hasAttribute('lazyload'),
      customControls: this.hasAttribute('custom-controls'),
      showCenteredPlayButton: this.hasAttribute('show-centered-play-button')
    }

    this.videoInnerElement.classList.add(...selectors.VIDEO_INNER_CLASS)

    if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
      this.videoInnerElement.appendChild(this.element)
    }

    if (this.type === selectors.VIDEO_TYPES.YOUTUBE || this.type === selectors.VIDEO_TYPES.VIMEO) {
      this.appendChild(this.videoInnerElement)
    }

    if (!this.src) {
      this.status.error = 'No video src'
    }

    if (this.options.playIntersection) {
      this.initWhenVisible()
    }
    this.unload = this.unload.bind(this)
    this.play = this.play.bind(this)
    this.pause = this.pause.bind(this)
    this.onPlay = this.onPlay.bind(this)
    this.onPause = this.onPause.bind(this)
    this.initWhenVisible = this.initWhenVisible.bind(this)

    if (this.hasAttribute('auto-init')) {
      this.init()
    }

    if (this.options.lazyload) {
      this.lazyload()
    }

    if (!this.options.controls || this.options.showCenteredPlayButton) {
      this.videoToggle()
    }

    if (this.options.autoplay) {
      this.visibilityChangeHandler = () => {
        if (document.visibilityState === 'visible' && !this.status.play) {
          this.onPlay()
        }
      }
      document.addEventListener('visibilitychange', this.visibilityChangeHandler)
    }
  }

  disconnectedCallback() {
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler)
    }

    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
  }

  get getVideoID() {
    if (this.getAttribute('data-external-id')) return this.getAttribute('data-external-id')

    if (/youtu\.?be/.test(this.src)) {
      const patterns = [
        /youtu\.be\/([^#&?]{11})/, // youtu.be/<id>
        /\?v=([^#&?]{11})/, // ?v=<id>
        /&v=([^#&?]{11})/, // &v=<id>
        /embed\/([^#&?]{11})/, // embed/<id>
        /\/v\/([^#&?]{11})/ // /v/<id>
      ]

      for (let i = 0; i < patterns.length; ++i) {
        if (patterns[i].test(this.src)) {
          return patterns[i].exec(this.src)[1]
        }
      }
    }

    if (/^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/))?([0-9]+)/.test(this.src)) {
      const parseURL = /^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/))?([0-9]+)/.exec(this.src)

      return parseURL[5]
    }

    return null
  }

  initWhenVisible() {
    this.observeVisibility(this).then((isVisible) => {
      if (!this.isLoaded && isVisible) {
        if (this.options.autoplay) {
          !this.thumbnailEl && this.initThumbnail()
        }
        this.src && this.type && this.init()
        this.isLoaded = true
      }
    })
  }

  unload() {
    switch (this.type) {
      case 'youtube':
        this.player.clearVideo()
        break
      case 'vimeo':
        this.player.unload()
        break
      default:
        this.player.pause()
        this.player.src = ''
        break
    }
  }

  initCustomControls() {
    const iconMute =
      '<svg xmlns="http://www.w3.org/2000/svg" class="size-6" viewBox="0 0 24 24" fill="none"><path d="M7.5 15.75H3a.75.75 0 0 1-.75-.75V9A.75.75 0 0 1 3 8.25h4.5L14.25 3v18L7.5 15.75Z" stroke="currentColor" stroke-width=".8" stroke-linecap="square"/><path d="m22.5 9.75-4.5 4.5M22.5 14.25 18 9.75" stroke="currentColor" stroke-width=".8" stroke-linecap="square" stroke-linejoin="round"/></svg>'
    const iconUnMute =
      '<svg xmlns="http://www.w3.org/2000/svg" class="size-6" viewBox="0 0 24 25" fill="none"><path d="M7.5 16.25H3a.75.75 0 0 1-.75-.75v-6A.75.75 0 0 1 3 8.75h4.5l6.75-5.25v18L7.5 16.25Z" stroke="currentColor" stroke-width=".8" stroke-linecap="square"/><path d="M18 10.25v4.5M21 8.75v7.5" stroke="currentColor" stroke-width=".8" stroke-linecap="square" stroke-linejoin="round"/></svg>'
    const iconPlay =
      '<svg xmlns="http://www.w3.org/2000/svg" class="size-6" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" d="m21.384 11.363-13.49-8.25a.75.75 0 0 0-1.144.637v16.5a.75.75 0 0 0 1.144.637l13.49-8.25a.74.74 0 0 0 0-1.274v0Z"/></svg>'
    const iconPause =
      '<svg xmlns="http://www.w3.org/2000/svg" class="size-6" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" d="M18.75 3.75h-3.375a.75.75 0 0 0-.75.75v15c0 .414.336.75.75.75h3.375a.75.75 0 0 0 .75-.75v-15a.75.75 0 0 0-.75-.75ZM8.625 3.75H5.25a.75.75 0 0 0-.75.75v15c0 .414.336.75.75.75h3.375a.75.75 0 0 0 .75-.75v-15a.75.75 0 0 0-.75-.75Z"/></svg>'
    const buttonSoundVideo = `
      <div class="absolute cursor-pointer z-20 flex justify-between w-[calc(100%_-_16px)] bottom-2 right-2 left-2">
        <div class="relative size-10 js-play-video">
          <div class="absolute top-0 left-0 hidden p-2 text-white [.is-playing_&]:!block">${iconPause}</div>
          <div class="absolute top-0 left-0 block p-2 text-white [.is-playing_&]:!hidden">${iconPlay}</div>
        </div>
         <div class="relative size-10 js-sound-video">
          <div class="absolute top-0 left-0 hidden p-2 text-white [.is-muted_&]:!block">${iconMute}</div>
          <div class="absolute top-0 left-0 block p-2 text-white [.is-muted_&]:!hidden">${iconUnMute}</div>
        </div>
      </div>
    `

    this.insertAdjacentHTML('beforeend', buttonSoundVideo)

    const buttonSoundVideoEls = selectAll(selectors.VIDEO_SOUND_SELECTOR, this)
    const buttonPlayVideoEls = selectAll(selectors.VIDEO_PLAY_SELECTOR, this)

    if (buttonSoundVideoEls.length) {
      buttonSoundVideoEls.forEach((item) => {
        on(
          'click',
          () => {
            if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
              if (this.options.muted) {
                this.player?.unMute()
              } else {
                this.player?.mute()
              }
            }

            if (this.type === selectors.VIDEO_TYPES.VIMEO) {
              if (this.options.muted) {
                this.player?.setVolume(1)
              } else {
                this.player?.setVolume(0)
              }
            }

            if (this.type === selectors.VIDEO_TYPES.MP4 && this.player) {
              this.player.muted = !this.options.muted
            }

            this.options.muted = !this.options.muted

            if (this.options.muted) {
              addClass(selectors.MUTED_CLASS, this)
            } else {
              removeClass(selectors.MUTED_CLASS, this)
            }
          },
          item
        )
      })
    }

    if (buttonPlayVideoEls.length) {
      buttonPlayVideoEls.forEach((item) => {
        on(
          'click',
          () => {
            if (!this.player) {
              this.setStatePlayer(selectors.STATES.LOADING)
              this.playOnClick = true

              if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
                this.setupYTPlayer()
              }

              if (this.type === selectors.VIDEO_TYPES.VIMEO) {
                this.setupVimeoPlayer()
              }

              if (this.type === selectors.VIDEO_TYPES.MP4) {
                this.setupMP4Player()
              }
            } else {
              // eslint-disable-next-line no-lonely-if
              if (!this.status.play) {
                this.onPlay()
              } else {
                this.onPause()
              }
            }
          },
          item
        )
      })
    }
  }

  initThumbnail() {
    const thumbnailImage = this.options.thumbnail
      ? `<img src="${this.options.thumbnail}" loading="${this.options.loading}" class="w-full h-full object-cover" alt="thumbnail video" />`
      : ''

    const html = `
      <div class="js-video-player-thumbnail video-player-thumbnail">
        ${thumbnailImage}
        ${
          !this.options.customControls
            ? `<button type="button" class="js-video-player-thumbnail-button rounded-full absolute size-12 lg:size-20 bg-transparent text-white flex z-7 items-center justify-center">
                <div class="[.is-loading_&]:hidden">
                  <span class="sr-only">Pause video</span>
                  <svg xmlns="http://www.w3.org/2000/svg" class="size-12 lg:size-20" viewBox="0 0 28 28" fill="none">
                    <path d="M24.948 13.256 9.21 3.631a.875.875 0 0 0-1.334.744v19.25a.875.875 0 0 0 1.334.744l15.74-9.625a.864.864 0 0 0 0-1.488Z" fill="currentColor" opacity=".8"/>
                  </svg>
                </div>
                <div class="hidden [.is-loading_&]:block">
                  <span class="button-loading button-loading-large"></span>
                </div>
              </button>`
            : ''
        }
      </div>
      `

    this.thumbnailEl = html
    this.insertAdjacentHTML('beforeend', this.thumbnailEl)

    const buttonEl = this.querySelector(selectors.VIDEO_THUMBNAIL_BUTTON_SELECTOR)

    if (this.options.autoplay) {
      this.setStatePlayer(selectors.STATES.LOADING)
    }
    buttonEl &&
      buttonEl.addEventListener('click', () => {
        if (!this.player) {
          this.setStatePlayer(selectors.STATES.LOADING)
          this.playOnClick = true

          if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
            this.setupYTPlayer()
          }

          if (this.type === selectors.VIDEO_TYPES.VIMEO) {
            this.setupVimeoPlayer()
          }

          if (this.type === selectors.VIDEO_TYPES.MP4) {
            this.setupMP4Player()
          }
        } else if (!this.status.play) {
          this.onPlay()
        } else {
          this.onPause()
        }
      })
  }

  initYoutubePlayer() {
    if (!this.options.autoplay && this.options.thumbnail) {
      !this.thumbnailEl && this.initThumbnail()
    } else {
      YT.ready(() => this.setupYTPlayer())
    }
  }

  initVimeoPlayer() {
    if (!this.options.autoplay && this.options.thumbnail) {
      !this.thumbnailEl && this.initThumbnail()
    } else {
      this.setupVimeoPlayer()
    }
  }

  initMP4Player() {
    if (this.querySelector('video')) return

    if (!this.options.autoplay && this.options.thumbnail) {
      !this.thumbnailEl && this.initThumbnail()
    } else {
      this.setupMP4Player()
    }
  }

  async initByType(type) {
    switch (type) {
      case selectors.VIDEO_TYPES.YOUTUBE:
        this.initYoutubePlayer()
        break
      case selectors.VIDEO_TYPES.VIMEO:
        this.initVimeoPlayer()
        break
      case selectors.VIDEO_TYPES.MP4:
        this.initMP4Player()
        break
      default:
        this.status.error = new Error('video type not supported')
        break
    }
  }

  async loadPlayerAPIs(type) {
    let src = ''

    if (type === selectors.VIDEO_TYPES.YOUTUBE && !window.YT) {
      src = 'https://www.youtube.com/iframe_api'
    } else if (type === selectors.VIDEO_TYPES.VIMEO && !window.Vimeo) {
      src = 'https://player.vimeo.com/api/player.js'
    } else {
      return
    }

    // eslint-disable-next-line consistent-return
    return loadScript(src)
  }

  setupYTPlayer() {
    const options = {
      videoId: this.getVideoID,
      playerVars: {
        autoplay: 0,
        controls: this.options.controls ? 1 : 0,
        height: '100%',
        width: '100%',
        playsinline: this.options.playsinline ? 1 : 0,
        loop: this.options.loop ? 1 : 0
      },
      events: {
        onError: () => {
          this.status.error = new Error('player could not be built')
        },
        onStateChange: (e) => this._onYouTubeStateChange(e),
        onReady: (e) => this._onYoutubeReadyState(e)
      }
    }

    if (YT.loaded) {
      this.player = new window.YT.Player(this.element, options)
    }
  }

  setupVimeoPlayer() {
    if (this.player) return

    const options = {
      id: this.getVideoID,
      autopause: false,
      ...this.options
    }

    this.player = new window.Vimeo.Player(this.videoInnerElement, options)

    this.player.ready().then(() => {
      this.scaleVideoToFill()

      if (this.options.autoplay || this.playOnClick) {
        this.play()
        this.displayThumbnail(false)
      }

      if (!this.options.autoplay && this.options.thumbnail) {
        this.play()
        this.setStatePlayer(selectors.STATES.PLAYING)
        this.displayThumbnail(false)
      }
    })

    this.player.on('play', async (data) => {
      const isPaused = await this.player.getPaused()
      const isEnded = await this.player.getEnded()
      const currentTime = data?.seconds || 0

      if ((!isPaused && !isEnded && currentTime > 0 && this.options.controls) || !this.options.controls) {
        setTimeout(() => {
          this.onPlay()
        }, 400)
      }
    })
    this.player.on('pause', () => this.onPause())
    this.player.on('ended', () => this.onEnd)
  }

  setupMP4Player() {
    this.videoEl = document.createElement('video')
    this.videoEl.src = this.src
    this.player = this.videoEl
    this.appendChild(this.videoEl)

    if (!this.options.autoplay && this.options.thumbnail) {
      this.play()
      this.setStatePlayer(selectors.STATES.PLAYING)
    }

    this.displayThumbnail(false)
    this.player.controls = this.options.controls
    this.player.autoplay = this.options.autoplay
    this.player.playsInline = this.options.playsinline
    this.player.muted = this.options.muted
    this.player.loop = this.options.loop

    this.player.addEventListener('play', () => {
      if (
        (!this.videoEl.paused && this.videoEl.currentTime > 0 && !this.videoEl.ended && this.options.controls) ||
        !this.options.controls
      ) {
        setTimeout(() => {
          this.onPlay()
        }, 400)
      }
    })
    this.player.addEventListener('pause', () => this.onPause())
    this.player.addEventListener('ended', () => this.onEnd())
  }

  play() {
    try {
      if (!this.player) return

      if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
        this.player.playVideo()
      } else {
        this.player.play()
      }
    } catch (error) {
      this.status.error = error
    }
  }

  pause() {
    try {
      if (!this.player) return

      if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
        this.player.pauseVideo()
      } else {
        this.player.pause()
      }
    } catch (error) {
      this.status.error = error
    }
  }

  onPlay() {
    if (this.status.play) {
      return
    }

    this.status.play = true
    this.toggleVideoMute()
    this.play()
    this.dispatchEvent(new CustomEvent('play'))
    this.setStatePlayer(selectors.STATES.PLAYING)
    this.displayThumbnail(false)
    this.imagePreviewEl.forEach((item) => {
      item.remove()
    })
  }

  onPause() {
    this.pause()
    this.status.play = false
    this.dispatchEvent(new CustomEvent('pause'))
    this.setStatePlayer(selectors.STATES.PAUSING)
    this.displayThumbnail(true)
  }

  onEnd() {
    if (this.options.loop) {
      this.play()
    } else {
      this.status.play = false
      this.dispatchEvent(new CustomEvent('ended'))
      this.displayThumbnail(true)
      this.setStatePlayer(null)
    }
  }

  setStatePlayer(status) {
    switch (status) {
      case selectors.STATES.PLAYING:
        this.classList.add(selectors.PLAYING_CLASS)
        this.classList.remove(selectors.PAUSING_CLASS)
        this.classList.remove(selectors.LOADING_CLASS)
        break
      case selectors.STATES.PAUSING:
        this.classList.add(selectors.PAUSING_CLASS)
        this.classList.remove(selectors.PLAYING_CLASS)
        this.classList.remove(selectors.LOADING_CLASS)
        break
      case selectors.STATES.LOADING:
        this.classList.add(selectors.LOADING_CLASS)
        this.classList.remove(selectors.PLAYING_CLASS)
        this.classList.remove(selectors.PAUSING_CLASS)
        break
      default:
        this.classList.remove(selectors.LOADING_CLASS)
        this.classList.remove(selectors.PLAYING_CLASS)
        this.classList.remove(selectors.PAUSING_CLASS)
    }
  }

  displayThumbnail(status) {
    const el = this.querySelector(selectors.VIDEO_THUMBNAIL_SELECTOR)

    if (!el) return

    if (status) {
      el.classList.remove(selectors.HIDDEN_CLASS)
      el.classList.add('visible')
      el.classList.remove('invisible')
    } else {
      el.classList.add(selectors.HIDDEN_CLASS)
      el.classList.remove('visible')
      el.classList.add('invisible')
    }
  }

  _onYouTubeStateChange(e) {
    const stateMap = {
      '-1': 'unstarted',
      0: 'ended',
      1: 'playing',
      2: 'pause',
      3: 'buffering',
      5: 'cued'
    }
    const state = stateMap[e.data.toString()]
    const eventMethodMap = {
      ended: this.onEnd,
      pause: this.onPause,
      playing: this.onPlay
    }

    if (eventMethodMap[state]) {
      const method = eventMethodMap[state]

      if (method) {
        method.call(this)
      }
    }
  }

  _onYoutubeReadyState() {
    this.scaleVideoToFill()

    if (this.options.autoplay || this.playOnClick) {
      this.setStatePlayer(selectors.STATES.PLAYING)
      this.onPlay()
      this.displayThumbnail(false)
    }

    if (this.options.muted && this.player?.mute) {
      this.player.mute()
    }
  }

  observeVisibility(element) {
    return new Promise((resolve) => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const isVisible = entry.isIntersecting

          resolve(isVisible)
        })
      })

      observer.observe(element)
    })
  }

  lazyload() {
    const observer = new IntersectionObserver(
      debounce((entries) => {
        entries.forEach((entry) => {
          if (!this.options.lazyload) return

          if (!entry.isIntersecting) {
            this.player && this.onPause()

            return
          }

          if (!this.isLoaded) {
            this.initWhenVisible()
          } else if (this.options.autoplay && !this.status.play) {
            this.player && this.onPlay()
          }

          observer.unobserve(this)
        })
      }, selectors.DEBOUNCE_TIME)
    )

    observer.observe(this)
  }

  videoToggle() {
    const showDefaultButton = !this.thumbnailEl && !this.options.thumbnail && !this.options.customControls
    const showButton = showDefaultButton || this.options.showCenteredPlayButton

    const html = showButton
      ? `<div class="absolute left-0 top-0 w-full h-full z-3 flex justify-center items-center cursor-pointer js-video-toggle ${this.options.controls ? '[.is-playing_&]:pointer-events-none' : ''}">
          <div class="rounded-full absolute size-12 lg:size-20 flex items-center justify-center bg-transparent text-white z-6 [.is-loading_&]:hidden [.is-playing_&]:hidden ${showDefaultButton ? '' : 'md:!hidden'}">
            <svg xmlns="http://www.w3.org/2000/svg" class="size-12 lg:size-20" viewBox="0 0 28 28" fill="none">
              <path d="M24.948 13.256 9.21 3.631a.875.875 0 0 0-1.334.744v19.25a.875.875 0 0 0 1.334.744l15.74-9.625a.864.864 0 0 0 0-1.488Z" fill="currentColor" opacity=".8"/>
            </svg>
          </div>
        </div>`
      : '<div class="absolute left-0 top-0 w-full h-full z-5 flex justify-center items-center cursor-pointer js-video-toggle"></div>'

    this.insertAdjacentHTML('beforeend', html)

    const buttonVideoToggle = select('.js-video-toggle', this)

    if (buttonVideoToggle) {
      on(
        'click',
        () => {
          if (!this.player) {
            if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
              this.setupYTPlayer()
            }

            if (this.type === selectors.VIDEO_TYPES.VIMEO) {
              this.setupVimeoPlayer()
            }

            if (this.type === selectors.VIDEO_TYPES.MP4) {
              this.setupMP4Player()
            }
          } else if (this.status.play) {
            this.onPause()
          } else {
            this.onPlay()
          }
        },
        buttonVideoToggle
      )
    }
  }

  toggleVideoMute() {
    if (!this.player) {
      console.warn('Player not initialized');

      return
    }

    if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
      if (this.options.muted) {
        this.player.mute()
      } else {
        this.player.unMute()
      }
    }

    if (this.type === selectors.VIDEO_TYPES.VIMEO) {
      if (this.options.muted) {
        this.player.setVolume(0)
      } else {
        this.player.setVolume(1)
      }
    }

    if (this.type === selectors.VIDEO_TYPES.MP4) {
      this.player.muted = this.options.muted
    }

    if (this.options.muted) {
      addClass(selectors.MUTED_CLASS, this)
    } else {
      removeClass(selectors.MUTED_CLASS, this)
    }
  }

  scaleVideoToFill() {
    if (!['youtube', 'vimeo'].includes(this.type || '')) return

    const parentComputedStyle = getComputedStyle(this.parentElement)
    const isFixedPosition = ['fixed', 'absolute'].includes(parentComputedStyle.position)

    if (isFixedPosition) {
      if (!this.resizeHandler) {
        this.resizeHandler = debounce(() => this.scaleVideoToFill(), selectors.DEBOUNCE_TIME)
        window.addEventListener('resize', this.resizeHandler)
      }

      this.applyVideoScale(window.innerWidth / window.innerHeight)

      return
    }

    const computedStyle = getComputedStyle(this)
    const hasMaxHeight = computedStyle.maxHeight && computedStyle.maxHeight !== 'none'

    if (hasMaxHeight) {
      const rect = this.getBoundingClientRect()
      const containerWidth = rect.width
      const containerHeight = rect.height

      if (!containerWidth || !containerHeight) return

      this.applyVideoScale(containerWidth / containerHeight)
    } else {
      const containerAspectRatio = computedStyle.aspectRatio

      if (!containerAspectRatio) return

      const [containerWidth, containerHeight] = containerAspectRatio.split('/').map(Number)

      if (!containerWidth || !containerHeight) return

      this.applyVideoScale(containerWidth / containerHeight)
    }

    if (!this.resizeHandler) {
      this.resizeHandler = debounce(() => this.scaleVideoToFill(), selectors.DEBOUNCE_TIME)
      window.addEventListener('resize', this.resizeHandler)
    }
  }

  applyVideoScale(calculatedContainerRatio) {
    const videoRatioDefault = selectors.DEFAULT_VIDEO_RATIO
    const scaleValue = Math.max(calculatedContainerRatio / videoRatioDefault, videoRatioDefault / calculatedContainerRatio)

    // Round up to nearest 0.05 to avoid sub-pixel gaps on Safari/Android
    // e.g., 1.33333 → 1.35, 1.77777 → 1.80
    const roundedScale = Math.ceil(scaleValue * 20) / 20

    this.videoInnerElement.style.transform = `scale(${roundedScale})`
  }

  async init() {
    this.type !== selectors.VIDEO_TYPES.MP4 && (await this.loadPlayerAPIs(this.type))
    this.initByType(this.type)

    if (this.options.customControls && !this.options.controls) {
      this.initCustomControls()
    }
  }
}

if (!customElements.get('video-player')) {
  customElements.define('video-player', VideoPlayer)
}
